import { compose } from "redux";
import { connect } from "react-redux";
import { Set, List, Map } from "immutable";
import React, { PureComponent } from "react";
import { Field } from "redux-form/immutable";
import { Grid, MenuItem, Typography, Box, Tooltip } from "@material-ui/core";

import {
  GROUP_FORM,
  FACILITY_TYPE,
  BOOKING_SYSTEM_TYPES,
} from "features/planner/planner.constants";

import {
  getFieldsFromState,
  getLocationGroupsFromState,
  getMobileFacilitiesFromState,
  getStationaryFacilitiesByFieldIdFromState,
} from "features/planner/planner.selectors";

import {
  CardMapper,
  CompanyMapper,
  facilityTypeToString,
  bookingSystemTypeToString,
} from "features/planner/planner.mappers";

import { EMPTY_LIST } from "app/app.constants";
import cardService from "services/card.service";
import dataService from "services/data.service";
import { required } from "utils/validation.util";
import TextField from "app/components/form/TextField";
import SelectEmployeeCardField from "app/components/form/SelectEmployeeCardField";
import SpecificationLinkField from "features/planner/components/SpecificationLinkField";

class LocationGroupForm extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      companies: EMPTY_LIST,
      operationSupervisors: EMPTY_LIST,
      customerServiceLeaders: EMPTY_LIST,
    };
  }

  groupByEmployeeId = cards =>
    Map(
      List(cards).map(card => [
        card.employeeId.toString(),
        CardMapper.from(card),
      ]),
    );

  componentDidMount() {
    Promise.all([
      dataService.getCompanies(),
      cardService.getCardsByJobTitles(["Operations Supervisor"]),
      cardService.getCardsByJobTitles([
        "Customer Service Leader",
        "Customer Service Manager",
      ]),
    ]).then(([companies, osCards, cslCards]) =>
      this.setState({
        companies: companies.map(CompanyMapper.from),
        operationSupervisors: this.groupByEmployeeId(osCards),
        customerServiceLeaders: this.groupByEmployeeId(cslCards),
      }),
    );
  }

  render() {
    const {
      fields,
      facilities,
      handleSubmit,
      visibleFields,
      locationGroups,
      selectedFieldId,
      selectedFacilityType,
    } = this.props;

    return (
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item container />
          <Grid item container>
            <Field
              name={GROUP_FORM.SPECIFICATION}
              component={SpecificationLinkField}
            >
              Manage location requirements
            </Field>
          </Grid>
          {visibleFields.includes(GROUP_FORM.FACILITY) && (
            <Grid container item direction="column">
              <Grid item>
                <Typography variant="h6">Location</Typography>
              </Grid>
              <Grid container item spacing={1}>
                <Grid item xs={4}>
                  <Field
                    select
                    required
                    label="Rig Type"
                    margin="none"
                    validate={[required]}
                    component={TextField}
                    name={GROUP_FORM.FACILITY_TYPE}
                  >
                    {Object.values(FACILITY_TYPE).map(type => (
                      <MenuItem key={type} value={type}>
                        {facilityTypeToString(type)}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
                {selectedFacilityType === FACILITY_TYPE.FIXED && (
                  <Grid item xs={4}>
                    <Field
                      select
                      required
                      label="Field"
                      margin="none"
                      component={TextField}
                      name={GROUP_FORM.FIELD}
                    >
                      {fields.map(field => (
                        <MenuItem key={field.get("id")} value={field.get("id")}>
                          {field.get("name")}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                )}
                <Grid item xs={4}>
                  {[FACILITY_TYPE.FIXED, FACILITY_TYPE.MOBILE].includes(
                    selectedFacilityType,
                  ) && (
                    <Field
                      select
                      required
                      label="Facility"
                      margin="none"
                      validate={[required]}
                      component={TextField}
                      name={GROUP_FORM.FACILITY_ID}
                      noResultsMessage="No facilities available"
                      disabled={
                        !selectedFieldId &&
                        selectedFacilityType === FACILITY_TYPE.FIXED
                      }
                    >
                      {facilities.map(facility => {
                        var locationGroupForFacilityAlreadyExists = locationGroups.some(
                          locationGroup =>
                            locationGroup.get("facilityType") ===
                              selectedFacilityType &&
                            locationGroup.get("facilityId") ===
                              facility.get("id"),
                        );

                        if (locationGroupForFacilityAlreadyExists) {
                          return (
                            <Tooltip
                              key={facility.get("id")}
                              title="A location group already exists for this facility"
                            >
                              <Box>
                                <MenuItem disabled value={facility.get("id")}>
                                  {facility.get("name")}
                                </MenuItem>
                              </Box>
                            </Tooltip>
                          );
                        }

                        return (
                          <MenuItem
                            key={facility.get("id")}
                            value={facility.get("id")}
                          >
                            {facility.get("name")}
                          </MenuItem>
                        );
                      })}
                    </Field>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid container spacing={1} item xs direction="column">
            <Grid item>
              <Typography variant="h6">Location Details</Typography>
            </Grid>
            <Grid item container>
              <Field
                select
                required
                label="Company"
                margin="none"
                validate={[required]}
                component={TextField}
                name={GROUP_FORM.COMPANY_ID}
              >
                {this.state.companies.map(company => (
                  <MenuItem key={company.get("id")} value={company.get("id")}>
                    {company.get("name")}
                  </MenuItem>
                ))}
              </Field>
            </Grid>
            <Grid container item spacing={1}>
              <Grid item xs>
                <Field
                  format={null}
                  component={SelectEmployeeCardField}
                  name={GROUP_FORM.CUSTOMER_SERVICE_LEAD}
                  cardsByEmployeeId={this.state.customerServiceLeaders}
                  TextFieldProps={{
                    margin: "none",
                    label: "Customer Service Leader",
                  }}
                />
              </Grid>
              <Grid item xs>
                <Field
                  format={null}
                  component={SelectEmployeeCardField}
                  name={GROUP_FORM.OPERATION_SUPERVISOR}
                  cardsByEmployeeId={this.state.operationSupervisors}
                  TextFieldProps={{
                    margin: "none",
                    label: "Operations Supervisor",
                  }}
                />
              </Grid>
            </Grid>
            <Grid container item spacing={1}>
              <Grid item xs>
                <Field
                  component={TextField}
                  name={GROUP_FORM.SHORT_NAME}
                  label="Short Name"
                  margin="none"
                />
              </Grid>
              <Grid item xs>
                <Field
                  component={TextField}
                  name={GROUP_FORM.INSTALLATION_PHONE}
                  label="Location Phone"
                  margin="none"
                />
              </Grid>
            </Grid>
            <Grid container item spacing={1}>
              <Grid item xs>
                <Field
                  component={TextField}
                  name={GROUP_FORM.OFFICE_PHONE}
                  label="Office Phone"
                  margin="none"
                />
              </Grid>
              <Grid item xs>
                <Field
                  component={TextField}
                  name={GROUP_FORM.WINCH_PHONE}
                  label="Winch Phone"
                  margin="none"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={1} item xs={5} direction="column">
            <Grid item>
              <Typography variant="h6">Booking Details</Typography>
            </Grid>
            <Grid item>
              <Field
                select
                margin="none"
                component={TextField}
                name={GROUP_FORM.BOOKING_SYSTEM_TYPE}
                label="Booking System"
              >
                {Object.values(BOOKING_SYSTEM_TYPES).map(type => (
                  <MenuItem key={type} value={type}>
                    {bookingSystemTypeToString(type)}
                  </MenuItem>
                ))}
              </Field>
            </Grid>
            <Grid item>
              <Field
                component={TextField}
                name={GROUP_FORM.BOOKING_CONTACT}
                label="Booking Contact"
                margin="none"
              />
            </Grid>
            <Grid item>
              <Field
                component={TextField}
                name={GROUP_FORM.BOOKING_PHONE}
                label="Booking Phone"
                margin="none"
              />
            </Grid>
            <Grid item>
              <Field
                component={TextField}
                name={GROUP_FORM.BOOKING_EMAIL}
                label="Booking Email"
                margin="none"
              />
            </Grid>
            <Grid item>
              <Field
                component={TextField}
                name={GROUP_FORM.AIRPORT}
                label="Airport"
                margin="none"
              />
            </Grid>
          </Grid>
        </Grid>
      </form>
    );
  }
}

LocationGroupForm.defaultProps = {
  facilities: EMPTY_LIST,
  visibleFields: Set(Object.values(GROUP_FORM)),
};

export default compose(
  connect((state, { formValueSelector }) => {
    const selectedFacilityType = formValueSelector(
      state,
      GROUP_FORM.FACILITY_TYPE,
    );

    const selectedFieldId = formValueSelector(state, GROUP_FORM.FIELD);

    return {
      selectedFieldId,
      selectedFacilityType,
      fields: getFieldsFromState(state),
      locationGroups: getLocationGroupsFromState(state),
      groupId: formValueSelector(state, GROUP_FORM.GROUP_ID),
      facilities:
        selectedFacilityType === FACILITY_TYPE.FIXED
          ? getStationaryFacilitiesByFieldIdFromState(state, selectedFieldId)
          : selectedFacilityType === FACILITY_TYPE.MOBILE
          ? getMobileFacilitiesFromState(state)
          : undefined,
    };
  }),
)(LocationGroupForm);
